import { type ISelectOption } from "../../../app-utils/form.js";
import { type Column, type FileType } from "../../../graphql.js";
import { ATTRIBUTE_TYPE, CELL_TYPE, COLUMN_DYNAMIC_TYPES } from "../../../settings/settings.js";
import { type ICellComponent } from "./cell-base-interfaces.js";
import { BooleanCellEditor } from "./cells/editors/BooleanCellEditor.js";
import { DateCellEditor } from "./cells/editors/DateCellEditor.js";
import { ColorCellEditor } from "./cells/editors/EditableColorCell.js";
import { FileCellEditor } from "./cells/editors/FileCellEditor.js";
import { IntegerCellEditor } from "./cells/editors/IntegerCellEditor.js";
import { NumberCellEditor } from "./cells/editors/NumberCellEditor.js";
import { OrganismCellEditor } from "./cells/editors/OrganismCellEditor.js";
import { PhenotypeCellEditor } from "./cells/editors/PhenotypeEditor.js";
import { SelectCellEditor } from "./cells/editors/SelectCellEditor.js";
import { StringCellEditor } from "./cells/editors/StringCellEditor.js";

export function getCellEditor(column: Partial<Column>): ICellComponent {
  if (column.dynamicType === COLUMN_DYNAMIC_TYPES.ORGANISM_PROPERTY) {
    switch (column.cellType) {
      case CELL_TYPE.STRING:
        return PhenotypeCellEditor;
      case CELL_TYPE.NUMBER:
        return NumberCellEditor;
      case CELL_TYPE.INTEGER:
        return IntegerCellEditor;
    }
  }

  if (column.dynamicType === COLUMN_DYNAMIC_TYPES.TRACKER_ATTRIBUTE) {
    switch (column.attribute?.attributeType.id) {
      case ATTRIBUTE_TYPE.FREE_TEXT:
        return StringCellEditor;
      case ATTRIBUTE_TYPE.FREE_NUMERICAL:
        return NumberCellEditor;
      case ATTRIBUTE_TYPE.CONTROLLED_TEXT:
      case ATTRIBUTE_TYPE.DYNAMIC_TERMS:
        return SelectCellEditor;
      case ATTRIBUTE_TYPE.ORGANISM:
        return OrganismCellEditor;
      case ATTRIBUTE_TYPE.DATE:
        return DateCellEditor;
      case ATTRIBUTE_TYPE.BOOLEAN:
        return BooleanCellEditor;
      case ATTRIBUTE_TYPE.FILE:
        return FileCellEditor;
    }
  }

  if (column.field === "vocabularyTerm.color") {
    return ColorCellEditor;
  }

  return StringCellEditor;
}

export interface CellEditorParams {
  selectOptions?: ISelectOption[];
  fileType?: FileType;
}

export function getCellEditorParams(column: Partial<Column>): CellEditorParams {
  if (column.dynamicType === COLUMN_DYNAMIC_TYPES.TRACKER_ATTRIBUTE) {
    switch (column.attribute?.attributeType.id) {
      case ATTRIBUTE_TYPE.CONTROLLED_TEXT: {
        const options: ISelectOption[] = column.attribute.vocabularyTerms.map((vocabularyTerm) => ({
          value: vocabularyTerm.id,
          label: vocabularyTerm.name,
        }));
        return { selectOptions: options };
      }
      case ATTRIBUTE_TYPE.DYNAMIC_TERMS: {
        const options: ISelectOption[] = column.attribute.dynamicTerms.map((dynamicTerm) => ({
          value: dynamicTerm.id,
          label: dynamicTerm.name,
        }));
        return { selectOptions: options };
      }
      case ATTRIBUTE_TYPE.FILE: {
        return { fileType: column.attribute.fileType };
      }
    }
  }
}
